import {
  Button,
  Checkbox,
  Empty,
  Form,
  Icons,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { useWatch } from 'antd/lib/form/Form';
import { ColumnProps } from 'antd/lib/table';
import {
  BatchInfo,
  ProductBatchDrawer,
  VariantAttribute,
  VariantAttributeConfigDrawer,
} from 'components/drawer';
import {
  VariantCustomField,
  VariantCustomFieldDrawer,
} from 'components/drawer/VariantCustomFieldDrawer';
import { useProductVariantSkuValidator } from 'hooks/useProductVariantSkuValidator';
import { IMeasurementUnit } from 'interfaces';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IPhoto, IVariantOfProduct } from 'services/product';
import { IAttribute, ITemplate } from 'services/template';
import { IVariant } from 'services/variant';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import {
  isPurchasePriceRequired,
  optionalPurchasePriceFlag,
  shouldShowPurchasePriceColumn,
} from 'utilities/product-purchase-price';
import { ProductTemplateTypeMap } from 'utilities/shared';
import { autoVariantsFlag, buildVariantRows } from 'utilities/variant-combinations';
import { VariantImageUploadFormItem } from './VariantImageUploadFormItem';

const PRODUCT_TYPE_OPTIONS = [
  {
    name: 'Product',
    value: 'PRODUCT',
  },
  {
    name: 'Material',
    value: 'MATERIAL',
  },
];

interface OptionType {
  id: string;
  name: string;
}

interface ProductFormProps {
  type: 'add' | 'edit';
  isFetching: boolean;
  isSubmitting: boolean;
  categoryOptions: OptionType[];
  tagOptions: OptionType[];
  warehouseOptions: OptionType[];
  templateOptions: ITemplate[];
  measurementUnitOptions: Omit<IMeasurementUnit, 'description'>[];
  productInfo?: IVariantOfProduct | null;
  handleBomTabEnabled?: (enabled: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (values: any) => void;
}

export const ProductForm = ({
  type,
  isFetching,
  isSubmitting,
  categoryOptions,
  tagOptions,
  warehouseOptions,
  templateOptions,
  measurementUnitOptions,
  productInfo,
  onDirtyChange,
  handleBomTabEnabled,
  onSubmit,
}: ProductFormProps) => {
  const [openProductBatchDrawer, setOpenProductBatchDrawer] = useState<boolean>(false);
  const [openVariantCustomFieldDrawer, setOpenVariantCustomFieldDrawer] = useState<boolean>(false);
  const [variantCustomFieldConfig, setVariantCustomFieldConfig] = useState<VariantCustomField[]>(
    [],
  );
  const [openVariantAttributeConfigDrawer, setOpenVariantAttributeConfigDrawer] =
    useState<boolean>(false);
  const [variantAttributeConfig, setVariantAttributeConfig] = useState<VariantAttribute[]>([]);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>();
  const validateVariantSkuCode = useProductVariantSkuValidator();
  const isSubmittingRef = useRef(false);
  const isHydratingProductInfoRef = useRef(false);

  const [form] = Form.useForm();
  const hasBatch = useWatch('hasBatch', form) ?? false;
  const iSellThisProduct = useWatch('sell', form) ?? false;
  const iPurchaseThisProduct = useWatch('purchase', form) ?? false;
  const optionalPurchasePriceEnabled = useFeatureFlag(optionalPurchasePriceFlag);
  const autoVariantsEnabled = useFeatureFlag(autoVariantsFlag);
  const variantDetails = useWatch('variantDetails', form) ?? [];
  const formTemplates = useWatch('variantTemplateConfig', form) ?? [];
  const hasVariant = useWatch('hasVariant', form);

  useEffect(() => {
    // Skip: this hasVariant change came from hydrating existing product data,
    // not from a user unchecking the "has variants" checkbox / clearing attributes.
    if (isHydratingProductInfoRef.current) {
      isHydratingProductInfoRef.current = false;
      return;
    }

    // Show only on row in the table when product has no variants selected
    if (!hasVariant) {
      setVariantAttributeConfig([]);
      handleVariantAttributeConfigDrawerSave([]);
      form.setFieldsValue({
        variantDetails: variantDetails.slice(0, 1),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVariant]);

  useEffect(() => {
    // Enable product recipe tab or not
    if (type === 'edit' && handleBomTabEnabled) {
      handleBomTabEnabled(productInfo?.make ?? false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productInfo]);

  useEffect(() => {
    if (!iPurchaseThisProduct) {
      const updated = variantDetails.map((variant: any) => ({
        ...variant,
        purchasePrice: 0,
      }));

      form.setFieldsValue({ variantDetails: updated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iPurchaseThisProduct]);

  useEffect(() => {
    if (productInfo && type === 'edit') {
      const customFieldConfig: VariantCustomField[] = (productInfo?.customFieldConfig ?? []).map(
        (field) => ({
          name: field.name,
          type: ProductTemplateTypeMap[field.type],
          options: field.options,
        }),
      );

      const variantAttributeConfig: VariantAttribute[] = (productInfo?.variantConfig ?? []).map(
        (field) => ({
          name: field.name,
          options: field.options,
        }),
      );

      const firstVariant: Omit<IVariant, 'productVariantPhoto'> & {
        photo: IPhoto;
      } = (productInfo?.productVariants?.[0] ?? []) as unknown as any;

      const productTags: string[] = firstVariant.tags.map((tag) => tag.name);
      const productCategory: string | undefined = firstVariant?.category?.name ?? undefined;
      const variantTemplateConfig: string[] = [];
      firstVariant.productTemplates.forEach((temp) => {
        if (temp.templateType === 'template') {
          variantTemplateConfig.push(temp.templateId);
        }
      });

      const variants = (productInfo?.productVariants ?? []).map((variant) => {
        let photo = variant.photo
          ? [{ uid: variant.photo.uid, name: variant.photo.name, url: variant.photo.url }]
          : null;

        let customFields: any = {};
        variant.customFields?.forEach((field) => {
          customFields = { ...customFields, ...field };
        });

        let templateFields: any = {};
        let attributeFields: any = {};
        variant.productTemplates.forEach((temp) => {
          if (temp.templateType === 'template') {
            let attribute: any = {};
            temp.productTemplateAttributes.forEach((attr) => {
              if (attr.attributeType === 'boolean') {
                attribute[attr.attributeId] = attr.value === 'true';
              } else {
                attribute[attr.attributeId] = attr.value;
              }
            });
            templateFields[temp.templateId] = attribute;
          }

          if (temp.templateType === 'variant') {
            temp.productTemplateAttributes.forEach((attr) => {
              attributeFields[attr.attributeName] = attr.value;
            });
          }
        });

        return {
          id: variant.productVariantId,
          sku: variant.sku,
          name: variant.productVariantName,
          photo,
          purchasePrice: variant.purchasePrice,
          sellingPrice: variant.sellingPrice,
          customFields,
          templateFields,
          attributeFields,
        };
      });

      const batchInfo = productInfo.batchInfo ?? null;
      // A product can have multiple variant rows even without variant attribute
      // config (e.g. imported data), so don't rely on variantConfig alone or the
      // extra rows get treated as "no variants" and dropped from the form.
      const hasVariant =
        (productInfo?.variantConfig ?? []).length > 0 ||
        (productInfo?.productVariants ?? []).length > 1;
      const hasBatch = !!batchInfo;

      setVariantCustomFieldConfig(customFieldConfig);
      setVariantAttributeConfig(variantAttributeConfig);
      setBatchInfo(batchInfo);

      // Only arm the guard if this will actually change `hasVariant` and thus
      // trigger the watcher effect below - otherwise the flag would never get
      // consumed and would incorrectly suppress a later, genuine user toggle.
      if (form.getFieldValue('hasVariant') !== hasVariant) {
        isHydratingProductInfoRef.current = true;
      }
      form.setFields([
        {
          name: 'name',
          value: productInfo?.productName,
        },
        {
          name: 'productType',
          value: productInfo?.productType.toUpperCase(),
        },
        {
          name: 'description',
          value: productInfo?.description,
        },
        {
          name: 'productCategory',
          value: productCategory,
        },
        {
          name: 'productTags',
          value: productTags,
        },
        {
          name: 'productWarehouses',
          value: productInfo?.productWarehouses ?? [],
        },
        {
          name: 'make',
          value: productInfo?.make ?? false,
        },
        {
          name: 'purchase',
          value: productInfo?.purchase ?? false,
        },
        {
          name: 'sell',
          value: productInfo?.sell ?? false,
        },
        {
          name: 'measurementUnit',
          value: productInfo?.measurementUnit?.abbreviation,
        },
        {
          name: 'hasBatch',
          value: hasBatch,
        },
        {
          name: 'hasVariant',
          value: hasVariant,
        },
        {
          name: 'variantTemplateConfig',
          value: variantTemplateConfig,
        },
        {
          name: 'variantDetails',
          value: variants,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productInfo, type]);

  const batchInfoTagTitle = useMemo(() => {
    if (!batchInfo) return '';

    const { batchNo, expirationDate, notes } = batchInfo;
    const title: string[] = [batchNo];

    if (expirationDate) {
      const formattedDate = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(expirationDate));
      title.push(formattedDate);
    }

    if (notes) {
      title.push(notes);
    }

    return title.join('-');
  }, [batchInfo]);

  const showProductBatchDrawer = () => {
    setOpenProductBatchDrawer(true);
  };

  const closeProductBatchDrawer = () => {
    setOpenProductBatchDrawer(false);
  };

  const showVariantCustomFieldDrawer = () => {
    setOpenVariantCustomFieldDrawer(true);
  };

  const closeVariantCustomFieldDrawer = () => {
    setOpenVariantCustomFieldDrawer(false);
  };

  const showVariantAttributeConfigDrawer = () => {
    setOpenVariantAttributeConfigDrawer(true);
  };

  const closeVariantAttributeConfigDrawer = () => {
    setOpenVariantAttributeConfigDrawer(false);
  };

  const variantTemplatesAttributes = useMemo(() => {
    const selectedTemplates = templateOptions.filter((template) =>
      formTemplates.includes(template.id),
    );
    const attributes = selectedTemplates.map(({ id, name, productAttribute }) => {
      const attributes = productAttribute.map((attr) => ({
        ...attr,
        templateId: id,
        templateName: name,
      }));
      return attributes;
    });
    return attributes.flat() as (IAttribute & { templateId: string; templateName: string })[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTemplates]);

  const handleTemplatesChange = (values: string[]) => {
    // remove stale data from form after removing template
    const newValues = variantDetails.map((variant: any) => {
      const templateIds = Object.keys(variant?.templateFields ?? {});
      templateIds.forEach((id) => {
        if (!values.includes(id)) {
          delete variant.templateFields[id];
        }
      });

      // if object is empty then set undefined
      variant.templateFields = Object.keys(variant?.templateFields ?? {}).length
        ? variant.templateFields
        : undefined;

      return variant;
    });
    form.setFieldsValue({ variantDetails: newValues });
  };

  const handleProductBatchDrawerSave = (batchInfo: BatchInfo | null) => {
    closeProductBatchDrawer();

    // unchecked batch track checkbox if there is not batch info provided
    if (!batchInfo) {
      form.setFieldsValue({ hasBatch: false });
    }

    setBatchInfo(batchInfo);
  };

  const handleVariantCustomFieldDrawerSave = (customFields: VariantCustomField[]) => {
    // remove stale data from form after removing custom field
    const newValues = variantDetails.map((variant: any) => {
      const names = Object.keys(variant?.customFields ?? {});
      const customFieldNames = customFields.map((item) => item.name);

      names.forEach((name) => {
        if (!customFieldNames.includes(name)) {
          delete variant.customFields[name];
        }
      });

      // if object is empty then set undefined
      variant.customFields = names.length ? variant.customFields : undefined;

      return variant;
    });

    setVariantCustomFieldConfig(customFields);
    closeVariantCustomFieldDrawer();
    form.setFieldsValue({ variantDetails: newValues });
  };

  const handleVariantAttributeConfigDrawerSave = (attributes: VariantAttribute[]) => {
    // unchecked hasVariant checkbox if there is no attributes info provided
    if (!attributes.length) {
      form.setFieldsValue({ hasVariant: false });
    }

    // Stitches S3: one row per option combination, keeping the rows the user already has
    if (autoVariantsEnabled) {
      setVariantAttributeConfig(attributes);
      closeVariantAttributeConfigDrawer();
      form.setFieldsValue({
        variantDetails: buildVariantRows({
          productName: form.getFieldValue('name') ?? '',
          attributes,
          existingRows: variantDetails,
        }),
      });
      return;
    }

    // remove stale data from form after removing attribute config
    const newValues = variantDetails.map((variant: any) => {
      const names = Object.keys(variant?.attributeFields ?? {});
      const attributeNames = attributes.map((item) => item.name);

      names.forEach((name) => {
        if (!attributeNames.includes(name)) {
          delete variant.attributeFields[name];
        }
      });

      // if object is empty then set undefined
      variant.attributeFields = names.length ? variant.attributeFields : undefined;

      return variant;
    });

    setVariantAttributeConfig(attributes);
    closeVariantAttributeConfigDrawer();
    form.setFieldsValue({ variantDetails: newValues });
  };

  const defaultColumns = useMemo(() => {
    // default columns in the table
    const columns: ColumnProps<any>[] = [
      {
        dataIndex: 'sku',
        title: 'Variant Code / SKU',
        render: (_value, _row, index) => {
          return (
            <Form.Item
              name={[index, 'sku']}
              validateTrigger="onChange"
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'SKU is required',
                },
                {
                  validator: async (rule, value) => {
                    if (isSubmittingRef.current) {
                      return Promise.resolve(); // skip backend call on submit
                    }

                    return validateVariantSkuCode(rule, value);
                  },
                },
                {
                  validator: async (_, value) => {
                    if (!value) return Promise.resolve();

                    const allVariants = form.getFieldValue('variantDetails') ?? [];
                    const duplicateCount = allVariants.filter(
                      (variant: any, i: number) =>
                        i !== index &&
                        variant?.sku?.trim().toLowerCase() === value.trim().toLowerCase(),
                    ).length;

                    if (duplicateCount > 0) {
                      return Promise.reject(new Error('SKU must be unique'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          );
        },
      },
      ...(autoVariantsEnabled
        ? [
            {
              dataIndex: 'name',
              title: 'Variant Name',
              render: (_value: any, _row: any, index: number) => (
                <Form.Item name={[index, 'name']}>
                  <Input style={{ width: '100%', marginTop: '1.2rem' }} />
                </Form.Item>
              ),
            },
          ]
        : []),
      {
        dataIndex: 'photo',
        title: 'Image',
        align: 'center',
        render: (_text: any, _record: any, index: number) => (
          <VariantImageUploadFormItem name={[index, 'photo']} maxCount={1} />
        ),
      },
    ];

    if (
      shouldShowPurchasePriceColumn({
        formType: type,
        purchasesProduct: iPurchaseThisProduct,
        optionalPurchasePriceEnabled,
      })
    ) {
      columns.push({
        dataIndex: 'purchasePrice',
        title: 'Default Purchase Price',
        render: (_text: any, _record: any, index: number) => (
          <Form.Item
            name={[index, 'purchasePrice']}
            rules={[
              {
                required: isPurchasePriceRequired({ optionalPurchasePriceEnabled }),
                message: 'Purchase Price is required',
              },
            ]}
          >
            <InputNumber min={0} style={{ width: '100%', marginTop: '1.2rem' }} />
          </Form.Item>
        ),
      });
    }

    if (iSellThisProduct) {
      columns.push({
        dataIndex: 'sellingPrice',
        title: 'Default Selling Price',
        render: (_text: any, _record: any, index: number) => (
          <Form.Item
            name={[index, 'sellingPrice']}
            rules={[
              {
                required: true,
                message: 'Selling Price is required',
              },
            ]}
          >
            <InputNumber min={0} style={{ width: '100%', marginTop: '1.2rem' }} />
          </Form.Item>
        ),
      });
    }

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iSellThisProduct, iPurchaseThisProduct, optionalPurchasePriceEnabled, autoVariantsEnabled]);

  const variantTemplateConfigColumns: ColumnProps<any>[] = variantTemplatesAttributes.map(
    (attr) => {
      if (attr.type === 'float') {
        return {
          dataIndex: `${attr.name}`,
          title: `${attr.name.toUpperCase()} (${attr.templateName})`,
          render: (text: any, record: any, index: number) => (
            <Form.Item name={[index, 'templateFields', attr.templateId, attr.id]}>
              <InputNumber style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          ),
        };
      }

      if (attr.type === 'boolean') {
        return {
          dataIndex: `${attr.name}`,
          title: `${attr.name.toUpperCase()} (${attr.templateName})`,
          render: (text: any, record: any, index: number) => (
            <Form.Item
              name={[index, 'templateFields', attr.templateId, attr.id]}
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          ),
        };
      }

      if (attr.type === 'dropdown') {
        return {
          dataIndex: `${attr.name}`,
          title: `${attr.name.toUpperCase()} (${attr.templateName})`,
          render: (text: any, record: any, index: number) => (
            <Form.Item name={[index, 'templateFields', attr.templateId, attr.id]}>
              <Select
                options={attr.options.map((op) => ({ label: op, value: op }))}
                style={{ width: '100%', marginTop: '1.2rem' }}
              />
            </Form.Item>
          ),
        };
      }

      return {
        dataIndex: `${attr.name}`,
        title: `${attr.name.toUpperCase()} (${attr.templateName})`,
        render: (value, row, index) => {
          return (
            <Form.Item name={[index, 'templateFields', attr.templateId, attr.id]}>
              <Input style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          );
        },
      };
    },
  );

  // populate all variant attributes columns in the table left side
  const variantAttributeConfigColumns: ColumnProps<any>[] = variantAttributeConfig.map((attr) => {
    return {
      dataIndex: `${attr.name}`,
      title: `${attr.name.toUpperCase()} (Variant)`,
      render: (text: any, record: any, index: number) => (
        <Form.Item
          name={[index, 'attributeFields', attr.name]}
          rules={[{ required: true, message: `${attr.name} is required` }]}
        >
          <Select
            options={attr.options.map((op) => ({ label: op, value: op }))}
            style={{ width: '100%', marginTop: '1.2rem' }}
          />
        </Form.Item>
      ),
    };
  });

  // populate all variant attributes columns in the table right side
  const variantCustomFieldConfigColumns: ColumnProps<any>[] = variantCustomFieldConfig.map(
    (field) => {
      if (field.type === 'Float') {
        return {
          dataIndex: `${field.name}`,
          title: `${field.name.toUpperCase()} (Custom Field)`,
          render: (text: any, record: any, index: number) => (
            <Form.Item name={[index, 'customFields', field.name]}>
              <InputNumber style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          ),
        };
      }

      if (field.type === 'Boolean') {
        return {
          dataIndex: `${field.name}`,
          title: `${field.name.toUpperCase()} (Custom Field)`,
          render: (text: any, record: any, index: number) => (
            <Form.Item
              name={[index, 'customFields', field.name]}
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          ),
        };
      }

      if (field.type === 'Dropdown') {
        return {
          dataIndex: `${field.name}`,
          title: `${field.name.toUpperCase()} (Custom Field)`,
          render: (text: any, record: any, index: number) => (
            <Form.Item name={[index, 'customFields', field.name]}>
              <Select
                options={field.options.map((op) => ({ label: op, value: op }))}
                style={{ width: '100%', marginTop: '1.2rem' }}
              />
            </Form.Item>
          ),
        };
      }

      return {
        dataIndex: `${field.name}`,
        title: `${field.name.toUpperCase()} (Custom Field)`,
        render: (value, row, index) => {
          return (
            <Form.Item name={[index, 'customFields', field.name]}>
              <Input style={{ width: '100%', marginTop: '1.2rem' }} />
            </Form.Item>
          );
        },
      };
    },
  );

  const tableColumns = [
    ...variantAttributeConfigColumns,
    ...defaultColumns,
    ...variantTemplateConfigColumns,
    ...variantCustomFieldConfigColumns,
  ];

  const handleFormSubmit = (formValues: any) => {
    onSubmit({
      ...formValues,
      batchInfo,
      productCategory: formValues.productCategory?.[0] ?? undefined,
      productTags: formValues.productTags ?? [],
      productWarehouses: formValues.productWarehouses ?? [],
      variantTemplateConfig: formValues.variantTemplateConfig ?? [],
      variantAttributeConfig: variantAttributeConfig.map((item) => ({
        ...item,
        type: 'Dropdown',
        required: false,
        allowCustomValue: false,
      })),
      variantCustomFieldConfig: variantCustomFieldConfig.map((item) => ({
        ...item,
        required: false,
        allowCustomValue: item.type === 'Text' || item.type === 'Float' ? true : false, // needs to rethink about this validation
      })),
    });
  };

  return (
    <div>
      <ProductBatchDrawer
        open={openProductBatchDrawer}
        batchInfo={batchInfo}
        onClose={closeProductBatchDrawer}
        onSave={handleProductBatchDrawerSave}
      />

      <VariantCustomFieldDrawer
        open={openVariantCustomFieldDrawer}
        customFields={variantCustomFieldConfig}
        onClose={closeVariantCustomFieldDrawer}
        onSave={handleVariantCustomFieldDrawerSave}
      />

      <VariantAttributeConfigDrawer
        open={openVariantAttributeConfigDrawer}
        attributes={variantAttributeConfig}
        onClose={closeVariantAttributeConfigDrawer}
        onSave={handleVariantAttributeConfigDrawerSave}
      />

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        // onFinish={handleFormSubmit}
        disabled={isSubmitting}
        onValuesChange={() => {
          onDirtyChange && onDirtyChange(true);
        }}
      >
        <Form.Item
          label="Product Name"
          name="name"
          rules={[{ required: true, message: 'Please enter product name' }]}
        >
          <Input placeholder="Product Name" />
        </Form.Item>

        <Form.Item
          label="Item Type"
          name="productType"
          rules={[{ required: true, message: 'Please select item type' }]}
        >
          <Select
            style={{ width: '100%' }}
            placeholder="Select item type"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {PRODUCT_TYPE_OPTIONS.map((type) => (
              <Select.Option key={type.value} value={type.value}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item label="Category" name="productCategory">
          <Select
            style={{ width: '100%' }}
            mode="tags"
            onChange={(value) => {
              // keep only last selected value
              const latestValue = value.slice(-1);
              form.setFieldsValue({ productCategory: latestValue });
            }}
            placeholder="Select Category"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            onSelect={(value) => {
              form.setFieldsValue({ category: [value] });
            }}
            notFoundContent={
              isFetching ? (
                <Spin
                  size="small"
                  style={{
                    width: '100%',
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : categoryOptions.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No categories found" />
              ) : null
            }
          >
            {categoryOptions.map((category) => (
              <Select.Option key={category.name} value={category.name}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Tags" name="productTags">
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Select or enter new tags"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={
              isFetching ? (
                <Spin
                  size="small"
                  style={{
                    width: '100%',
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : null
            }
          >
            {tagOptions.map((tag) => (
              <Select.Option key={tag.name} value={tag.name}>
                {tag.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Warehouses" name="productWarehouses">
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select warehouses"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={
              isFetching ? (
                <Spin
                  size="small"
                  style={{
                    width: '100%',
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : warehouseOptions.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No warehouse found" />
              ) : null
            }
          >
            {warehouseOptions.map((warehouse) => (
              <Select.Option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Units Of Measure" name="measurementUnit">
          <Select
            style={{ width: '100%' }}
            placeholder="Select measurement unit"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={
              isFetching ? (
                <Spin
                  size="small"
                  style={{
                    width: '100%',
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : measurementUnitOptions.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No measurement unit found"
                />
              ) : null
            }
          >
            {measurementUnitOptions.map((unit) => (
              <Select.Option key={unit.id} value={unit.abbreviation}>
                {`${unit.name} (${unit.abbreviation})`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Product Source Section */}
        <Space direction="vertical" style={{ marginTop: '3rem', display: 'flex', gap: '0.5rem' }}>
          <Typography.Text style={{ fontSize: '0.875rem', fontWeight: 700 }}>
            Product Source
          </Typography.Text>

          <Space>
            {/* Most tenants buy and sell; making a product is opt-in. */}
            <Form.Item name="make" valuePropName="checked" noStyle initialValue={false}>
              <Checkbox>I make this product</Checkbox>
            </Form.Item>

            <Form.Item name="purchase" valuePropName="checked" noStyle initialValue={true}>
              <Checkbox>I buy this product</Checkbox>
            </Form.Item>

            <Form.Item name="sell" valuePropName="checked" noStyle initialValue={true}>
              <Checkbox>I sell this product</Checkbox>
            </Form.Item>
          </Space>
        </Space>

        {/* Batch Info Section */}
        <Space direction="vertical" style={{ marginTop: '3rem', display: 'flex', gap: '0.5rem' }}>
          <Typography.Text style={{ fontSize: '0.875rem', fontWeight: 700 }}>
            Do you track quantities in batch or lot numbers?
          </Typography.Text>

          <Form.Item name="hasBatch" valuePropName="checked" noStyle initialValue={false}>
            <Popconfirm
              onConfirm={() => {
                form.setFieldsValue({ hasBatch: false, batchInfo: null });
                setBatchInfo(null);
              }}
              disabled={!hasBatch}
              title="Are you sure product has no batch?"
            >
              <Checkbox
                checked={hasBatch}
                onClick={() => {
                  if (!hasBatch) {
                    form.setFieldsValue({ hasBatch: true });
                    showProductBatchDrawer();
                  }
                }}
              >
                I track batch / lot numbers{' '}
                {batchInfo && <Tag style={{ marginLeft: '0.5rem' }}>{batchInfoTagTitle}</Tag>}
              </Checkbox>
            </Popconfirm>
          </Form.Item>

          {/* Template Config Section */}
          <div
            style={{
              display: 'flex',
              gap: '3rem',
              alignItems: 'flex-start',
              marginTop: '3rem',
            }}
          >
            <div style={{ flex: 1 }}>
              <Form.Item label="Custom Fields From Template" name="variantTemplateConfig">
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select templates"
                  filterOption={(input, option: any) =>
                    option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={handleTemplatesChange}
                  notFoundContent={
                    isFetching ? (
                      <Spin
                        size="small"
                        style={{
                          width: '100%',
                          height: 50,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      />
                    ) : templateOptions.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No template found" />
                    ) : null
                  }
                >
                  {templateOptions.map((template) => (
                    <Select.Option key={template.id} value={template.id}>
                      {template.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {variantTemplatesAttributes.map((attr) => (
                  <Tag key={attr.id}>{attr.name}</Tag>
                ))}
              </div>
            </div>
          </div>
        </Space>

        {/* Custom Field Config Section */}
        <div style={{ marginTop: '2rem' }}>
          <Typography.Text style={{ fontSize: '0.875rem', fontWeight: 700 }}>
            Add custom fields
          </Typography.Text>
          <Button onClick={showVariantCustomFieldDrawer} style={{ marginLeft: '1rem' }}>
            Add
          </Button>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {variantCustomFieldConfig.map((field) => (
              <Tag
                closable
                key={field.name}
                onClose={() => {
                  setVariantCustomFieldConfig((prev) => prev.filter((f) => f.name !== field.name));
                }}
              >
                {field.name}
              </Tag>
            ))}
          </div>
        </div>

        {/* Variant Attribute Config Section */}
        <Space
          direction="vertical"
          style={{ marginTop: '2rem', display: 'flex', gap: '0.5rem', marginBottom: '4rem' }}
        >
          <Typography.Text style={{ fontSize: '0.875rem', fontWeight: 700 }}>
            Does this product come in different color, size or similar?
          </Typography.Text>

          <Form.Item name="hasVariant" valuePropName="checked" noStyle initialValue={false}>
            <Popconfirm
              onConfirm={() => {
                form.setFieldsValue({ hasVariant: !hasVariant });
              }}
              disabled={!hasVariant}
              title="Are you sure product has no variant?"
            >
              <Checkbox
                checked={hasVariant}
                onClick={() => {
                  if (!hasVariant) {
                    form.setFieldsValue({ hasVariant: true });
                    showVariantAttributeConfigDrawer();
                  }
                }}
              >
                Yes, this product has multiple variants{' '}
              </Checkbox>
            </Popconfirm>
          </Form.Item>
        </Space>

        <Button
          onClick={showVariantAttributeConfigDrawer}
          style={{ marginBottom: '1rem' }}
          disabled={!hasVariant}
          hidden={!hasVariant}
        >
          Open Variant Config
        </Button>

        <Form.List name="variantDetails">
          {(variants, { add, remove }) => {
            const columns = [...tableColumns];
            columns.push({
              dataIndex: 'action',
              title: 'Action',
              align: 'center',
              render: (value, row, index) => {
                return (
                  <Button
                    icon={<Icons.DeleteOutlined />}
                    danger
                    type="text"
                    onClick={() => remove(row.name)}
                    disabled={variants.length === 1}
                  />
                );
              },
            });

            // @ts-ignore
            variants = variants.length === 0 ? [{}] : variants;

            return (
              <Space direction="vertical" style={{ display: 'flex' }}>
                <Table
                  bordered
                  rowKey="id"
                  dataSource={variants}
                  columns={columns}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
                <Form.Item>
                  <Button
                    onClick={() => add()}
                    disabled={!hasVariant}
                    style={{ display: 'block', width: '100%' }}
                  >
                    +
                  </Button>
                </Form.Item>
              </Space>
            );
          }}
        </Form.List>

        <Form.Item style={{ textAlign: 'right', marginTop: '2rem' }}>
          <Button
            type="primary"
            // htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={async () => {
              try {
                isSubmittingRef.current = true;
                await form.validateFields();
                const values = form.getFieldsValue();
                handleFormSubmit(values);
              } catch (e: any) {
                // scroll to first error field
                const firstErrorField = e?.errorFields?.[0]?.name;
                if (firstErrorField) {
                  form.scrollToField(firstErrorField);
                }
              } finally {
                isSubmittingRef.current = false;
              }
            }}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
